import { Container, Graphics, Text, TextStyle } from "pixi.js";
import { Card } from "./Card";
import { CardBackgroundPattern } from "./CardBackgroundPattern";

export type PileType = "tableau" | "foundation" | "stock" | "waste";

export interface PileConfig {
  type: PileType;
  x: number;
  y: number;
  cardSpacing?: number;
  label?: string;
}

export class Pile extends Container {
  public readonly type: PileType;
  public readonly cards: Card[] = [];
  public cardSpacing: number;
  private placeholder: Graphics;
  private label?: Text;
  private completionLabel?: Text;

  constructor(config: PileConfig) {
    super();
    this.type = config.type;
    this.x = config.x;
    this.y = config.y;
    this.cardSpacing = config.cardSpacing ?? 0;

    // Set contextual name for debugging
    if (config.type === "tableau") {
      // Tableau piles will get index from GameBoard
      this.name = `Pile-${config.type}`;
    } else if (config.type === "foundation") {
      // Foundation piles will get suit label from GameBoard
      this.name = `Pile-${config.type}`;
    } else {
      this.name = `Pile-${config.type}`;
    }

    this.placeholder = this.createPlaceholder();
    this.placeholder.name = `Placeholder-${config.type}`;
    this.addChild(this.placeholder);

    if (config.label) {
      this.label = this.createLabel(config.label);
      this.label.name = `Label-${config.type}`;
      this.addChild(this.label);
    }
  }

  private createPlaceholder(): Graphics {
    const placeholder = new Graphics();
    // Use same corner radius as cards for consistency
    placeholder.roundRect(0, 0, 80, 120, CardBackgroundPattern.CORNER_RADIUS);
    placeholder.fill(0x0d4a24);
    placeholder.stroke({ width: 2, color: 0x2a7d4e });
    return placeholder;
  }

  private createLabel(text: string): Text {
    // Use smaller font for text labels (like "STOCK", "WASTE") vs suit symbols
    const fontSize = text.length > 1 ? 16 : 48;
    
    const label = new Text({
      text,
      style: new TextStyle({
        fontFamily: "Arial",
        fontSize,
        fill: 0x1a7a47,
        align: "center",
        fontWeight: text.length > 1 ? "bold" : "normal",
      }),
    });
    label.x = 40;
    label.y = 60;
    label.anchor.set(0.5, 0.5);
    return label;
  }

  addCard(card: Card): void {
    this.cards.push(card);
    this.updateCardPosition(card);
    this.addChild(card);
    
    // For stock piles, ensure the top card is always on top for interaction
    if (this.type === "stock" && this.cards.length > 0) {
      const topCard = this.cards[this.cards.length - 1];
      this.removeChild(topCard);
      this.addChild(topCard); // Bring to front
    }
    
    this.updateCompletionLabel();
  }

  removeCard(card: Card): Card | null {
    const index = this.cards.indexOf(card);
    if (index === -1) return null;
    
    this.cards.splice(index, 1);
    this.removeChild(card);
    this.updateCompletionLabel();
    return card;
  }

  removeCardsFrom(card: Card): Card[] {
    const index = this.cards.indexOf(card);
    if (index === -1) return [];
    
    const removed = this.cards.splice(index);
    removed.forEach(c => this.removeChild(c));
    this.updateCompletionLabel();
    return removed;
  }

  getTopCard(): Card | null {
    return this.cards.length > 0 ? this.cards[this.cards.length - 1] : null;
  }

  isEmpty(): boolean {
    return this.cards.length === 0;
  }

  private updateCardPosition(card: Card): void {
    const index = this.cards.indexOf(card);
    if (index === -1) return;

    switch (this.type) {
      case "tableau":
        card.x = 0;
        card.y = index * this.cardSpacing;
        break;
      case "foundation":
      case "stock":
        card.x = 0;
        card.y = 0;
        break;
      case "waste":
        const offset = Math.min(index, 2) * 20;
        card.x = offset;
        card.y = 0;
        break;
    }
  }

  updateAllCardPositions(): void {
    this.cards.forEach(card => this.updateCardPosition(card));
  }

  revealTopCard(): void {
    const topCard = this.getTopCard();
    if (topCard && !topCard.faceUp) {
      topCard.faceUp = true;
    }
  }

  setPlaceholderInteractive(handler: () => void): void {
    this.placeholder.eventMode = "static";
    this.placeholder.cursor = "pointer";
    this.placeholder.on("pointerdown", handler);
  }

  private updateCompletionLabel(): void {
    // Only show completion label for foundation piles
    if (this.type !== "foundation") return;

    const isComplete = this.cards.length === 13;

    if (isComplete && !this.completionLabel) {
      // Create completion label
      this.completionLabel = new Text({
        text: "✓ COMPLETE",
        style: new TextStyle({
          fontFamily: "Arial",
          fontSize: 14,
          fill: 0xffd700, // Gold color
          fontWeight: "bold",
          stroke: { color: 0x000000, width: 2 },
        }),
      });
      this.completionLabel.x = 40;
      this.completionLabel.y = 100;
      this.completionLabel.anchor.set(0.5, 0.5);
      this.completionLabel.name = "CompletionLabel";
      this.addChild(this.completionLabel);
    } else if (!isComplete && this.completionLabel) {
      // Remove completion label
      this.removeChild(this.completionLabel);
      this.completionLabel = undefined;
    }
  }
}

